// Mounts the 13-mode studio editor for a real project: loads it through
// ProjectProvider (engine registration, persistence, store hydration), reads
// the persisted game dimension from the project config, and exposes the
// project to the editor chrome via context.
import { createContext, useContext, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { takeLegacyLocalPrefabs, usePrefabStore, type Prefab, type StudioProject } from '../../engine';
import { useAuth } from '../auth/AuthProvider';
import { initialsOf } from '../app/screens-app';
import { ProjectProvider, useProject } from '../project/ProjectProvider';
import { loadModeDocument, saveModeDocument } from '../services/docs';
import { createDebouncedSaver } from './debouncedSave';
import { ScreenEditor } from './screens-editor';
import { useToast } from '../design/toast';

/**
 * Keeps the prefab library in the project's docs store: hydrates the engine
 * store on entry (lifting any pre-project localStorage prefabs once) and
 * debounce-saves changes, flushing on unmount and tab close like every other
 * mode document.
 */
function usePrefabPersistence(projectId: string) {
  const notify = useToast();
  useEffect(() => {
    let cancelled = false;
    const saver = createDebouncedSaver<Prefab[]>((prefabs, { keepalive }) => {
      void saveModeDocument(projectId, 'prefabs', { prefabs }, { keepalive }).catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Save failed.';
        notify(`Prefab library didn't save: ${message}`, 'error');
      });
    }, 1500);

    let unsubscribe: (() => void) | null = null;
    void loadModeDocument<{ prefabs?: Prefab[] }>(projectId, 'prefabs')
      .then((doc) => {
        if (cancelled) return;
        const legacy = takeLegacyLocalPrefabs();
        const prefabs = [...(doc.data.prefabs ?? []), ...legacy];
        usePrefabStore.getState().hydratePrefabs(prefabs);
        if (legacy.length > 0) saver.schedule(prefabs);
        // Persist every change after hydration.
        unsubscribe = usePrefabStore.subscribe((state, previous) => {
          if (state.prefabs !== previous.prefabs) saver.schedule(state.prefabs);
        });
      })
      .catch(() => {
        // Offline: the library still works in-memory for the session, but a
        // change made now would overwrite the real stored library on next
        // save once back online, so make that risk visible.
        if (cancelled) return;
        notify("Couldn't load your prefab library - working from an empty one for this session. Reload once you're back online, before saving any new prefabs.", 'error');
      });

    const onPageHide = () => saver.flush(true);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      cancelled = true;
      window.removeEventListener('pagehide', onPageHide);
      unsubscribe?.();
      saver.flush();
      usePrefabStore.getState().hydratePrefabs([]);
    };
  }, [projectId, notify]);
}

export interface EditorHostValue {
  projectId: string;
  project: StudioProject;
  /** Base path of this project's editor, e.g. /app/editor/<id>. */
  editorBase: string;
  /** Base path of this project's workspace, e.g. /app/project/<id>. */
  workspaceBase: string;
  /** Signed-in creator's initials for the top-bar avatar. */
  userInitials: string;
}

const EditorHostContext = createContext<EditorHostValue | null>(null);

export function useEditorHost(): EditorHostValue {
  const ctx = useContext(EditorHostContext);
  if (!ctx) throw new Error('useEditorHost must be used inside EditorRoute.');
  return ctx;
}

// Exported so a dev-only fixture route (see src/studio/dev) can mount the
// real editor chrome against a fixture project without duplicating this
// wiring.
// The dev-only fixture route (src/studio/dev/EditorFixtureRoute.tsx) mounts
// this same component under its own path, /dev/editor-fixture/:mode, which
// has no :id segment at all. Hardcoding editorBase to /app/editor/<id>
// regardless of the mounted route works fine for real navigation (the URL
// segment updates in place, matching route), but any navigation built from
// it - including Workspace's own onActivePanelChange - would otherwise
// carry the fixture over onto the real, API-backed route on the very next
// URL change, which then 404s on a project id that only exists in memory.
const FIXTURE_ROUTE_BASE = '/dev/editor-fixture';

export function HostedEditor({ tab }: { tab: string }) {
  const { project } = useProject();
  const { user } = useAuth();
  const location = useLocation();
  const projectId = project.meta.id;
  usePrefabPersistence(projectId);
  const isFixtureRoute = location.pathname.startsWith(FIXTURE_ROUTE_BASE);
  const value: EditorHostValue = {
    projectId,
    project,
    editorBase: isFixtureRoute ? FIXTURE_ROUTE_BASE : `/app/editor/${projectId}`,
    workspaceBase: `/app/project/${projectId}`,
    userInitials: initialsOf(user?.displayName || user?.email || '?'),
  };
  const dim = project.config.projection === '2D' ? '2d' : '3d';
  return (
    <EditorHostContext.Provider value={value}>
      <ScreenEditor tab={tab} dim={dim} />
    </EditorHostContext.Provider>
  );
}

/** Route element for /app/editor/:id/:mode. */
export function EditorRoute() {
  const { id = '', mode = 'edit' } = useParams();
  return (
    <ProjectProvider projectId={id}>
      <HostedEditor tab={mode} />
    </ProjectProvider>
  );
}
